import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import ms from 'ms';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import { queryKeysUser } from './queryKeys';

const EXCLUDED_KEYS = new Set([
  'accesstoken',
  'auth',
  'authtoken',
  'bearer',
  'credentials',
  'idtoken',
  'jwt',
  'login',
  'refreshtoken',
  'session',
  'sessiontoken',
  'token',
]);

/**
 * Captures all localStorage data excluding sensitive (authentication) keys.
 *
 * @returns {Record<string, string>} An object containing all non-sensitive localStorage data.
 */
export const captureLocalStorageData = (): Record<string, string> => {
  try {
    const storageData: Record<string, string> = {};

    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);

      if (!key) {
        continue;
      }

      if (EXCLUDED_KEYS.has(key.toLowerCase())) {
        continue;
      }

      const value = localStorage.getItem(key);
      if (value !== null) {
        storageData[key] = value;
      }
    }

    return storageData;
  } catch (error) {
    // ? what are we returning here?
    // TODO: check this

    return {};
  }
};

/**
 * Custom hook for backing up localStorage data to user settings.
 *
 * @returns {Object} An object containing the backup function and mutation state.
 * @returns {Function} returns.backupLocalStorage - Function to trigger localStorage backup.
 * @returns {Error | null} returns.error - Error from the backup mutation.
 * @returns {boolean} returns.isPending - Whether the backup is in progress.
 * @returns {boolean} returns.isSuccess - Whether the backup was successful.
 */
export const useBackupLocalStorage = () => {
  const updateUserSettingsMutation = useMutationUpdateCurrentUserSettings();

  const backupLocalStorage = useCallback(async () => {
    const localStorageData = captureLocalStorageData();

    if (Object.keys(localStorageData).length === 0) {
      log.warn({
        message: 'No localStorage data to back up',
      });
      return;
    }

    try {
      await updateUserSettingsMutation.mutateAsync({
        localStorageBackup: {
          data: localStorageData,
          timestamp: new Date().toISOString(),
          version: 1,
        },
      });
    } catch (error) {
      // Best-effort background backup: callers sit behind a debounce and cannot
      // handle a rejection, and the next backup interval retries anyway.
      // Rate-limit rejections (429, shared per-user budget) are expected noise.
      log.warn({
        message: 'localStorage backup skipped',
        data: { error },
      });
    }
  }, [updateUserSettingsMutation]);

  const debouncedBackupLocalStorage = useMemo(
    () => debounce(backupLocalStorage, ms('3s')),
    [backupLocalStorage],
  );

  return {
    backupLocalStorage: debouncedBackupLocalStorage,
    error: updateUserSettingsMutation.error,
    isPending: updateUserSettingsMutation.isPending,
    isSuccess: updateUserSettingsMutation.isSuccess,
  };
};

/**
 * Restores localStorage data from a backup.
 *
 * @param {Object} backupData - The backup data to restore from.
 * @param {Record<string, string>} backupData.data - The localStorage data to restore.
 * @param {string} backupData.timestamp - The timestamp when the backup was created.
 * @param {number} backupData.version - The version of the backup format.
 */
export const restoreLocalStorageFromBackup = (backupData: {
  data: Record<string, string>;
  timestamp: string;
  version: number;
}) => {
  if (!backupData?.data) {
    return;
  }

  for (const [key, value] of Object.entries(backupData.data)) {
    localStorage.setItem(key, value);
  }
};

/**
 * Custom hook for automatically backing up localStorage data.
 *
 * @param {Object} options - Configuration options for auto-backup behavior.
 * @param {boolean} [options.enabled=true] - Whether auto-backup is enabled.
 * @param {boolean} [options.onAppStart=true] - Whether to backup on app start.
 * @param {boolean} [options.onBeforeUnload=true] - Whether to backup before page unload.
 */
export const useAutoBackupLocalStorage = (
  options: {
    backupInterval?: number;
    enabled?: boolean;
    onAppStart?: boolean;
    onBeforeUnload?: boolean;
  } = {},
) => {
  const { backupLocalStorage } = useBackupLocalStorage();
  const { backupInterval, enabled, onAppStart, onBeforeUnload } = options;
  const hasRunInitialBackup = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (onAppStart) {
      void (async () => {
        await backupLocalStorage();
      })();
    }

    if (onBeforeUnload) {
      const handleBeforeUnload = () => {
        const localStorageData = captureLocalStorageData();

        sessionStorage.setItem(
          'localStorage_backup_pending',
          JSON.stringify({
            data: localStorageData,
            timestamp: new Date().toISOString(),
          }),
        );
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }

    if (backupInterval && typeof backupInterval === 'number') {
      let isBackupInProgress = false;

      const runBackup = async () => {
        if (isBackupInProgress) {
          return;
        }

        try {
          isBackupInProgress = true;

          log.info({
            message: 'Auto-backup localStorage data',
          });

          await backupLocalStorage();
        } finally {
          isBackupInProgress = false;
        }
      };

      // Run first backup immediately (only once)
      if (!hasRunInitialBackup.current) {
        hasRunInitialBackup.current = true;
        void runBackup();
      }

      // Run periodic backups
      const interval = setInterval(
        runBackup,
        Math.max(backupInterval, ms('30s')),
      );

      return () => {
        clearInterval(interval);
      };
    }
  }, [backupLocalStorage, backupInterval, enabled, onAppStart, onBeforeUnload]);
};

export type UserSettingsPayload = Record<string, unknown>;
export type UserSettingsPayloadWithBackupOptions = UserSettingsPayload & {
  skipBackup?: boolean;
};

type UserSettingsResponse = {
  code: string;
  message: string;
  requestId: string;
};

/**
 * Updates user settings.
 *
 * @param {Object} payload - An object containing the user settings to update.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/update_user_settings_user_settings_post
 */
export const updateCurrentUserSettings = async (
  payload: UserSettingsPayload,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.USER.UPDATE_USER_SETTINGS(), { json: payload })
    .json<UserSettingsResponse>();

  return response;
};

/**
 * Custom hook for updating the user settings of a current user.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationUpdateCurrentUserSettings = (
  options?: UseMutationOptions<
    UserSettingsPayload,
    Error,
    UserSettingsPayloadWithBackupOptions
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    async mutationFn(payload: UserSettingsPayloadWithBackupOptions) {
      const { skipBackup, ...restPayload } = payload;

      const payloadWithBackup =
        skipBackup === true // Only skip backing up localStorage data if set explicitly
          ? restPayload
          : {
              ...restPayload,
              localStorageBackup: {
                data: captureLocalStorageData(),
                timestamp: new Date().toISOString(),
                version: 1,
              },
            };

      return updateCurrentUserSettings(payloadWithBackup);
    },
    async onSuccess(data, variables, context) {
      // Refetch only after a successful mutation: on failure (e.g. 429) the server state
      // didn't change, and invalidating would revert optimistic updates, re-triggering
      // grid events and re-issuing the failed request in a loop (DNI-860).
      await queryClient.invalidateQueries({
        queryKey: [queryKeysUser.base(), 'data'],
      });
      await options?.onSuccess?.(data, variables, context);
    },
  });
};
